import Backbone from "backbone";
import Parse from "parse";
import FOVCalculator from "../helpers/FOVCalculator";
import SkySafariWebWidget from "../helpers/SkySafariWebWidget";
import Equipment from "../models/Equipment";
import BaseParseObject from "./BaseParseObject";

("use strict");

// Constants for FOV calculations and display
const FOV_CONSTANTS = {
	// Mathematical constants
	RAD_TO_DEG: 180 / Math.PI,
	MICRONS_TO_MM: 1000,
	DEFAULT_FOCAL_LENGTH_ADJUST: 1,
	DECIMAL_PLACES: 2,

	// Error states
	ERROR_VALUE: -1,

	// Display strings
	DEGREE_SYMBOL: "&deg;",
	MISSING_STATIC_INDICATOR: "[MISSING-STATIC]",
	CUSTOM_FOV_PREFIX: "Custom Field of View",
	NO_MAGNIFICATION_MESSAGE: "no magnification found",
	EQUIPMENT_SEPARATOR: " + ",

	// Error messages
	ERROR_MESSAGES: {
		INVALID_CUSTOM_FOV: "Invalid custom field of view value",
		MISSING_EQUIPMENT_PROPERTIES: "Equipment missing required properties",
		INVALID_EQUIPMENT_COMBINATION: "Invalid equipment combination",
		CALCULATION_FAILED: "FOV calculation failed",
		EQUIPMENT_ACCESS_ERROR: "Unable to access equipment data",
		INVALID_NUMERIC_VALUE: "Invalid numeric value provided",
		EQUIPMENT_NOT_FOUND: "Equipment not found or inaccessible"
	},

	// Validation constants
	VALIDATION: {
		MAX_FOV_DEGREES: 360,
		MIN_FOV_DEGREES: 0.001,
		MAX_MAGNIFICATION: 10000,
		MIN_MAGNIFICATION: 0.1
	}
};

// Equipment types configuration
const EQUIPMENT_TYPES = [
	"telescope",
	"eyepiece",
	"camera",
	"barlowReducer",
	"binoculars",
	"guideChip"
];



export const FOVIndicator = BaseParseObject.extend("FOVIndicator", {
	initParseACL: function () {
		const acl = new Parse.ACL();

		if (!this.get("ACL")) {
			acl.setPublicReadAccess(false);
			acl.setReadAccess(Parse.User.current().id, true);
			acl.setWriteAccess(Parse.User.current().id, true);
			this.setACL(acl);
		}
	},

	/**
	 * Helper method to get the display version of equipment (static or instance)
	 * @param {string} equipmentType - The type of equipment to retrieve
	 * @returns {Object|null} The equipment object to display
	 */
	getDisplayEquipment: function (equipmentType) {
		try {
			if (!equipmentType || typeof equipmentType !== 'string') {
				console.warn("FOVIndicator: Invalid equipment type provided:", equipmentType);
				return null;
			}

			const equipment = this.get(equipmentType);
			if (!equipment) {
				return null;
			}

			// Return static equipment if available, otherwise return the equipment instance
			return equipment.get("staticEquipment") || equipment;
		} catch (error) {
			console.error(`FOVIndicator: Error accessing ${equipmentType} equipment:`, error);
			return null;
		}
	},

	/**
	 * Sets display properties for all equipment types
	 * Replaces repetitive code with a generic approach
	 */
	setEquipmentProperties: function () {
		// Set display properties for all equipment types
		EQUIPMENT_TYPES.forEach(equipmentType => {
			const displayPropertyName = `display${equipmentType.charAt(0).toUpperCase()}${equipmentType.slice(1)}`;
			this[displayPropertyName] = this.getDisplayEquipment(equipmentType);
		});
	},

	/**
	 * Validates custom FOV values
	 * @returns {Object} Validation result with isValid flag and errors array
	 */
	validateCustomFOV: function () {
		const errors = [];
		const customFOV = this.get("customFOV");
		const customFOVHeight = this.get("customFOVHeight");

		// Validate width
		if (customFOV === undefined || customFOV === null) {
			errors.push("Custom FOV width is required");
		} else {
			const fovValue = parseFloat(customFOV);
			if (Number.isNaN(fovValue)) {
				errors.push("Custom FOV width must be a valid number");
			} else if (fovValue <= FOV_CONSTANTS.VALIDATION.MIN_FOV_DEGREES) {
				errors.push(`Custom FOV width must be greater than ${FOV_CONSTANTS.VALIDATION.MIN_FOV_DEGREES} degrees`);
			} else if (fovValue > FOV_CONSTANTS.VALIDATION.MAX_FOV_DEGREES) {
				errors.push(`Custom FOV width cannot exceed ${FOV_CONSTANTS.VALIDATION.MAX_FOV_DEGREES} degrees`);
			}
		}

		// Validate height (optional)
		if (customFOVHeight !== undefined && customFOVHeight !== null && customFOVHeight !== 0) {
			const heightValue = parseFloat(customFOVHeight);
			if (Number.isNaN(heightValue)) {
				errors.push("Custom FOV height must be a valid number");
			} else if (heightValue < 0) {
				errors.push("Custom FOV height cannot be negative");
			} else if (heightValue > FOV_CONSTANTS.VALIDATION.MAX_FOV_DEGREES) {
				errors.push(`Custom FOV height cannot exceed ${FOV_CONSTANTS.VALIDATION.MAX_FOV_DEGREES} degrees`);
			}
		}

		return {
			isValid: errors.length === 0,
			errors: errors
		};
	},

	/**
	 * Generates display name for custom field of view
	 * @returns {string} Custom FOV display name
	 */
	getCustomFOVDisplayName: function () {
		try {
			const validation = this.validateCustomFOV();

			if (!validation.isValid) {
				console.warn("FOVIndicator: Invalid custom FOV values:", validation.errors);
				return `${FOV_CONSTANTS.CUSTOM_FOV_PREFIX} (${FOV_CONSTANTS.ERROR_MESSAGES.INVALID_CUSTOM_FOV})`;
			}

			const customFOV = parseFloat(this.get("customFOV"));
			const customFOVHeight = parseFloat(this.get("customFOVHeight"));

			if (customFOVHeight > 0) {
				return (
					FOV_CONSTANTS.CUSTOM_FOV_PREFIX + " (" +
					customFOV.toFixed(FOV_CONSTANTS.DECIMAL_PLACES) +
					" x " +
					customFOVHeight.toFixed(FOV_CONSTANTS.DECIMAL_PLACES) +
					")"
				);
			}

			return `${FOV_CONSTANTS.CUSTOM_FOV_PREFIX} ('${customFOV.toFixed(FOV_CONSTANTS.DECIMAL_PLACES)}')`;
		} catch (error) {
			console.error("FOVIndicator: Error generating custom FOV display name:", error);
			return `${FOV_CONSTANTS.CUSTOM_FOV_PREFIX} (${FOV_CONSTANTS.ERROR_MESSAGES.CALCULATION_FAILED})`;
		}
	},

	/**
	 * Validates equipment has required properties for naming
	 * @param {Object} equipment - Equipment object to validate
	 * @param {string} equipmentType - Type of equipment for error reporting
	 * @returns {boolean} True if equipment has valid name property
	 */
	validateEquipmentForDisplay: (equipment, equipmentType) => {
    if (!equipment) {
      return false;
    }

    try {
      const name = equipment.get("name");
      if (!name || typeof name !== 'string' || name.trim() === '') {
        console.warn(`FOVIndicator: ${equipmentType} missing valid name property`);
        return false;
      }
      return true;
    } catch (error) {
      console.error(`FOVIndicator: Error accessing ${equipmentType} name:`, error);
      return false;
    }
  },

	/**
	 * Builds equipment combination name for display
	 * @returns {string} Equipment combination name
	 */
	getEquipmentCombinationName: function () {
		try {
			let comboName = "";

			// telescope base
			if (this.displayTelescope && this.validateEquipmentForDisplay(this.displayTelescope, "telescope")) {
				comboName = this.displayTelescope.get("name");
			} else if (this.displayTelescope) {
				comboName = "Telescope (name unavailable)";
			}

			// add eyepiece or camera
			if (this.displayEyepiece && this.validateEquipmentForDisplay(this.displayEyepiece, "eyepiece")) {
				comboName += `${FOV_CONSTANTS.EQUIPMENT_SEPARATOR}${this.displayEyepiece.get("name")}`;
			} else if (this.displayEyepiece) {
				comboName += `${FOV_CONSTANTS.EQUIPMENT_SEPARATOR}Eyepiece (name unavailable)`;
			} else if (this.displayCamera && this.validateEquipmentForDisplay(this.displayCamera, "camera")) {
				comboName += `${FOV_CONSTANTS.EQUIPMENT_SEPARATOR}${this.displayCamera.get("name")}`;
			} else if (this.displayCamera) {
				comboName += `${FOV_CONSTANTS.EQUIPMENT_SEPARATOR}Camera (name unavailable)`;
			}

			// add optional barlow/focal reducer
			if (this.displayBarlowReducer && this.validateEquipmentForDisplay(this.displayBarlowReducer, "barlowReducer")) {
				comboName += `${FOV_CONSTANTS.EQUIPMENT_SEPARATOR}${this.displayBarlowReducer.get("name")}`;
			} else if (this.displayBarlowReducer) {
				comboName += `${FOV_CONSTANTS.EQUIPMENT_SEPARATOR}Barlow/Reducer (name unavailable)`;
			}

			return comboName || "Equipment (names unavailable)";
		} catch (error) {
			console.error("FOVIndicator: Error building equipment combination name:", error);
			return "Equipment (error accessing names)";
		}
	},

	getDisplayName: function () {
		try {
			this.setEquipmentProperties();

			// custom field of view
			if (this.get("useCustomFOV")) {
				return this.getCustomFOVDisplayName();
			}

			// binoculars (standalone equipment)
			if (this.displayBinoculars) {
				if (this.validateEquipmentForDisplay(this.displayBinoculars, "binoculars")) {
					return this.displayBinoculars.get("name");
				} else {
					return "Binoculars (name unavailable)";
				}
			}

			// equipment combinations (telescope + accessories)
			return this.getEquipmentCombinationName();
		} catch (error) {
			console.error("FOVIndicator: Error generating display name:", error);
			return "FOV Indicator (error generating name)";
		}
	},

	getFOV: function () {
		try {
			this.setEquipmentProperties();

			const equipment = {
				telescope: this.displayTelescope,
				eyepiece: this.displayEyepiece,
				camera: this.displayCamera,
				barlowReducer: this.displayBarlowReducer,
				binoculars: this.displayBinoculars
			};

			const customSettings = {
				useCustomFOV: this.get("useCustomFOV"),
				customFOV: this.get("customFOV"),
				customFOVHeight: this.get("customFOVHeight")
			};

			// Validate custom FOV if being used
			if (customSettings.useCustomFOV) {
				const validation = this.validateCustomFOV();
				if (!validation.isValid) {
					console.warn("FOVIndicator: Custom FOV validation failed:", validation.errors);
					Backbone.trigger("errors:fovIndicator", {
						message: "Invalid custom FOV settings",
						errors: validation.errors
					});
					return { ...FOVCalculator.ERROR_FOV };
				}
			}

			const result = FOVCalculator.calculateFOV(equipment, customSettings);

			// Validate the calculation result
			if (!FOVCalculator.isValidFOV(result)) {
				console.warn("FOVIndicator: FOV calculation produced invalid result:", result);
				Backbone.trigger("errors:fovIndicator", {
					message: "FOV calculation produced invalid result",
					result: result
				});
			}

			return result;
		} catch (error) {
			console.error("FOVIndicator: Error calculating FOV:", error);
			Backbone.trigger("errors:fovIndicator", {
				message: FOV_CONSTANTS.ERROR_MESSAGES.CALCULATION_FAILED,
				originalError: error
			});

			return { ...FOVCalculator.ERROR_FOV };
		}
	},

	getFOVString: function () {
		try {
			const fov = this.getFOV();

			// Check for error state
			if (FOVCalculator.isErrorFOV(fov)) {
				return FOV_CONSTANTS.MISSING_STATIC_INDICATOR;
			}

			// Validate FOV before formatting
			if (!FOVCalculator.isValidFOV(fov)) {
				console.warn("FOVIndicator: Invalid FOV for string formatting:", fov);
				return FOV_CONSTANTS.MISSING_STATIC_INDICATOR;
			}

			// Use FOVCalculator formatting utility
			const formattedFOV = FOVCalculator.formatFOVForDisplay(fov, FOV_CONSTANTS.DECIMAL_PLACES);

			// Handle formatting errors
			if (formattedFOV === 'Invalid FOV') {
				return FOV_CONSTANTS.MISSING_STATIC_INDICATOR;
			}

			// Replace degree symbol format for consistency with existing display
			return formattedFOV.replace(/°/g, FOV_CONSTANTS.DEGREE_SYMBOL);
		} catch (error) {
			console.error("FOVIndicator: Error formatting FOV string:", error);
			return FOV_CONSTANTS.MISSING_STATIC_INDICATOR;
		}
	},

	getMagnification: function () {
		try {
			this.setEquipmentProperties();

			const equipment = {
				telescope: this.displayTelescope,
				eyepiece: this.displayEyepiece,
				barlowReducer: this.displayBarlowReducer,
				binoculars: this.displayBinoculars
			};

			const result = FOVCalculator.calculateMagnificationTotal(equipment);

			// Validate magnification result
			if (typeof result === 'number') {
				if (result < FOV_CONSTANTS.VALIDATION.MIN_MAGNIFICATION ||
					result > FOV_CONSTANTS.VALIDATION.MAX_MAGNIFICATION) {
					console.warn("FOVIndicator: Magnification result outside reasonable range:", result);
					Backbone.trigger("errors:fovIndicator", {
						message: "Magnification calculation produced unrealistic result",
						magnification: result
					});
				}
			}

			return result;
		} catch (error) {
			console.error("FOVIndicator: Error calculating magnification:", error);
			Backbone.trigger("errors:fovIndicator", {
				message: "Magnification calculation failed",
				originalError: error
			});
			return FOV_CONSTANTS.NO_MAGNIFICATION_MESSAGE;
		}
	},

	setEquipmentInstance: function (equipmentType, equipmentId) {
		try {
			if (!equipmentType || typeof equipmentType !== 'string') {
				throw new Error("Invalid equipment type provided");
			}

			if (!equipmentId || typeof equipmentId !== 'string') {
				throw new Error("Invalid equipment ID provided");
			}

			const equipInstance = Equipment.createWithoutData(equipmentId);
			this.set(equipmentType, equipInstance);
		} catch (error) {
			console.error(`FOVIndicator: Error setting ${equipmentType} equipment instance:`, error);
			Backbone.trigger("errors:fovIndicator", {
				message: `Failed to set ${equipmentType} equipment`,
				equipmentType: equipmentType,
				equipmentId: equipmentId,
				originalError: error
			});
		}
	},

	toggleShowFOV: function () {
		try {
			const currentState = this.get("fovOn");
			if (typeof currentState !== 'boolean') {
				console.warn("FOVIndicator: Invalid fovOn state, defaulting to false:", currentState);
			}

			const showFOV = !currentState;
			this.set("fovOn", showFOV);

			this.save().then(
				(item) => {
					Backbone.trigger("fovIndicators:updated", item);
				},
				(error) => {
					console.error("FOVIndicator: Error saving FOV toggle state:", error);
					Backbone.trigger("errors:parseReturned", error);
					Backbone.trigger("errors:fovIndicator", {
						message: "Failed to save FOV visibility toggle",
						originalError: error
					});
				},
			);
		} catch (error) {
			console.error("FOVIndicator: Error toggling FOV visibility:", error);
			Backbone.trigger("errors:fovIndicator", {
				message: "Failed to toggle FOV visibility",
				originalError: error
			});
		}
	},

	/**
	 * Helper method to handle equipment setting/unsetting in update method
	 * @param {Object} data - The data object containing equipment IDs
	 * @param {string} equipmentType - The type of equipment (e.g., 'telescope')
	 * @param {string} idKey - The key for the equipment ID in data (e.g., 'telescopeId')
	 */
	updateEquipmentInstance: function (data, equipmentType, idKey) {
		try {
			if (!data || typeof data !== 'object') {
				throw new Error("Invalid data object provided");
			}

			if (data[idKey]) {
				this.setEquipmentInstance(equipmentType, data[idKey]);
			} else {
				this.unset(equipmentType);
			}
		} catch (error) {
			console.error(`FOVIndicator: Error updating ${equipmentType} equipment:`, error);
			Backbone.trigger("errors:fovIndicator", {
				message: `Failed to update ${equipmentType} equipment`,
				equipmentType: equipmentType,
				idKey: idKey,
				originalError: error
			});
		}
	},

	update: function (data) {
		try {
			if (!data || typeof data !== 'object') {
				throw new Error("Invalid data object provided for update");
			}

			this.initParseACL();

			// Validate and set user
			const currentUser = Parse.User.current();
			if (!currentUser) {
				console.warn("FOVIndicator: No current user found during update");
			} else {
				this.set("user", currentUser);
			}

			// Validate and set custom FOV settings
			this.set("useCustomFOV", Boolean(data.useCustomFOV));

			// Validate numeric values
			const customFOV = parseFloat(data.customFOV);
			const customFOVHeight = parseFloat(data.customFOVHeight);

			if (data.useCustomFOV) {
				if (Number.isNaN(customFOV)) {
					throw new Error("Invalid custom FOV value provided");
				}
				if (data.customFOVHeight !== undefined && data.customFOVHeight !== null && Number.isNaN(customFOVHeight)) {
					throw new Error("Invalid custom FOV height value provided");
				}
			}

			this.set("customFOV", Number.isNaN(customFOV) ? 0 : customFOV);
			this.set("customFOVHeight", Number.isNaN(customFOVHeight) ? 0 : customFOVHeight);
			this.set("fovOn", Boolean(data.fovOn));

			// Equipment mapping for cleaner update logic
			const equipmentUpdates = [
				{ type: "telescope", idKey: "telescopeId" },
				{ type: "eyepiece", idKey: "eyepieceId" },
				{ type: "camera", idKey: "cameraId" },
				{ type: "barlowReducer", idKey: "barlowReducerId" },
				{ type: "binoculars", idKey: "binocularsId" },
				{ type: "guideChip", idKey: "guideChipId" }
			];

			// Update all equipment instances using the helper method
			equipmentUpdates.forEach(({ type, idKey }) => {
				this.updateEquipmentInstance(data, type, idKey);
			});

			this.save().then(
				(item) => {
					Backbone.trigger("fovIndicators:updated", item);

					// Update FOV indicators in any active SkySafari widget instances
					try {
						const skySafariObjects = window.Portal?.Current?.skySafariObjects;
						if (skySafariObjects && SkySafariWebWidget?.updateFOVIndicators) {
							Object.values(skySafariObjects).forEach((ssww) => {
								if (ssww) {
									SkySafariWebWidget.updateFOVIndicators(ssww);
								}
							});
						}
					} catch (widgetError) {
						console.warn("FOVIndicator: Error updating SkySafari widget:", widgetError);
					}
				},
				(error) => {
					console.error("FOVIndicator: Error saving FOV indicator:", error);
					Backbone.trigger("errors:parseReturned", error);
					Backbone.trigger("errors:fovIndicator", {
						message: "Failed to save FOV indicator",
						originalError: error
					});
				},
			);
		} catch (error) {
			console.error("FOVIndicator: Error updating FOV indicator:", error);
			Backbone.trigger("errors:fovIndicator", {
				message: "Failed to update FOV indicator",
				originalError: error
			});
		}
	},

	delete: function () {
		// Validate current state before deletion
		if (!this.id) {
			console.warn("FOVIndicator: Attempting to delete FOV indicator without ID");
			Backbone.trigger("errors:fovIndicator", {
				message: "Cannot delete FOV indicator: No ID found"
			});
			return Promise.reject(new Error("Cannot delete FOV indicator: No ID found"));
		}

		// Set the object's `deleted` status to true (soft delete)
		this.set("deleted", true);

		return this.save().then(
			(result) => {
				Backbone.trigger("fovIndicators:deleted", result);
			},
			(error) => {
				console.error("FOVIndicator: Error saving delete status:", error);
				// The remote operation failed. Error is an instance of Parse.Error.
				Backbone.trigger("errors:parseReturned", error);
				Backbone.trigger("errors:fovIndicator", {
					message: "Failed to delete FOV indicator",
					originalError: error
				});
			},
		);
	},
});

export default FOVIndicator;
